import React, { useEffect } from "react";
import { CheckCircle2, MessageCircle, X } from "lucide-react";
import { BUSINESS } from "@/data/site";

/**
 * Success modal shown after a booking is submitted (either via the bot API
 * or the WhatsApp fallback).
 */
const SuccessModal = ({ open, onClose, booking, mode = "api" }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="success-modal"
      className="fixed inset-0 z-[100] grid place-items-center bg-black/55 px-4 py-8 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-md rounded-3xl bg-white p-7 shadow-2xl ring-1 ring-[var(--royal)]/15">
        <button
          data-testid="success-modal-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-[var(--muted-foreground)] hover:bg-[var(--background)]"
        >
          <X size={18} />
        </button>

        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366]/15 text-[#16a34a]">
          <CheckCircle2 size={28} />
        </div>

        <h3 className="mt-5 font-display text-2xl font-bold text-[var(--royal-deep)]">
          {mode === "api" ? "Booking confirmed!" : "Booking sent on WhatsApp!"}
        </h3>
        <p className="mt-2 text-sm text-[var(--muted-foreground)]">
          {mode === "api"
            ? "We've messaged our concierge team and you'll receive a WhatsApp confirmation in a few seconds."
            : "Your booking details have opened in WhatsApp. Tap send to finish the request — our team will reply within minutes."}
        </p>

        {booking?.id && (
          <div className="mt-5 rounded-xl bg-[var(--background)] p-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-[var(--muted-foreground)]">Reference</span>
              <span className="font-semibold text-[var(--royal-deep)]">{booking.id}</span>
            </div>
            {booking.pickupDate && (
              <div className="mt-1.5 flex items-center justify-between">
                <span className="text-[var(--muted-foreground)]">Pickup</span>
                <span className="font-medium text-[var(--royal-deep)]">
                  {booking.pickupDate} {booking.pickupTime ? `· ${booking.pickupTime}` : ""}
                </span>
              </div>
            )}
            {booking.carType && (
              <div className="mt-1.5 flex items-center justify-between">
                <span className="text-[var(--muted-foreground)]">Vehicle</span>
                <span className="font-medium text-[var(--royal-deep)]">{booking.carType}</span>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <a
            data-testid="success-modal-wa"
            href={`https://wa.me/91${BUSINESS.primaryWhatsApp}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-white"
          >
            <MessageCircle size={14} /> Chat with us
          </a>
          <button
            data-testid="success-modal-ok"
            onClick={onClose}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full gradient-royal px-5 py-3 text-sm font-semibold text-white"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;
